import {
    getViewSettingsSafely,
    UserViewSettings,
    UserViewSettingsProp,
    ViewIdentifiable
} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import { ofType, StateObservable} from "redux-observable";
import { Observable, of } from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import {APP_CONFIG} from "../../config/GeneralConfig";
import {getUrlParamsFromViewSettings,  RouteMethods} from "../../config/RouteConfig";
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {UserAttendanceTime, UserAttendanceTimeModule} from "../../models/timesheet/UserAttendanceTime";
import {ActiveDataFilter} from "../../models/common/DataFilter";
import {TimesheetModule} from "../../models/timesheet/Timesheet";


export type UserAttendanceTimeViewActionTypes = FetchUserAttendanceTimeActionTypes | UpdateUserAttendanceTimeActionTypes

/**
 * FETCH TIMESHEET ENTRIES
 */
export interface FetchUserAttendanceTimeAction extends ViewIdentifiable, ReduxAction<typeof FETCH_USER_ATTENDANCE_TIME> {
    userViewSettings: Partial<UserViewSettings>
}
export interface FetchUserAttendanceTimeSuccessAction extends ViewIdentifiable, ReduxAction< typeof FETCH_USER_ATTENDANCE_TIME_SUCCESS > {
    payload: ApiReturnType< UserAttendanceTime[] >
}
export interface FetchUserAttendanceTimeErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_USER_ATTENDANCE_TIME_ERROR > {}

export type FetchUserAttendanceTimeActionTypes = FetchUserAttendanceTimeErrorAction | FetchUserAttendanceTimeSuccessAction | FetchUserAttendanceTimeAction

export const FETCH_USER_ATTENDANCE_TIME = "FETCH_USER_ATTENDANCE_TIME";
export const FETCH_USER_ATTENDANCE_TIME_SUCCESS = "FETCH_USER_ATTENDANCE_TIME_SUCCESS";
export const FETCH_USER_ATTENDANCE_TIME_ERROR = "FETCH_USER_ATTENDANCE_TIME_ERROR";

export const fetchUserAttendanceTime = (
    userViewSettings: Partial< UserViewSettings >,
    viewIdentifier: string
): FetchUserAttendanceTimeAction => ({ type: FETCH_USER_ATTENDANCE_TIME, viewIdentifier, userViewSettings });
export const fetchUserAttendanceTimeSuccess = ( payload: ApiReturnType< UserAttendanceTime[] >, viewIdentifier: string ): FetchUserAttendanceTimeSuccessAction =>
    ({ type: FETCH_USER_ATTENDANCE_TIME_SUCCESS,  payload, viewIdentifier });
export const fetchUserAttendanceTimeError = ( error: Error, viewIdentifier: string ): FetchUserAttendanceTimeErrorAction =>
    ({ type: FETCH_USER_ATTENDANCE_TIME_ERROR, error, viewIdentifier });

export const fetchUserAttendanceTimeEpic = (
    action$: Observable< FetchUserAttendanceTimeAction >,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( FETCH_USER_ATTENDANCE_TIME ),
        mergeMap( action => {
            const viewSettingsRedux = getViewSettingsSafely( action.viewIdentifier, store$ );

            const userViewSettings: UserViewSettings = {
                ...viewSettingsRedux,
                ...action.userViewSettings,
                activeFilters: [
                    ...viewSettingsRedux.activeFilters,
                    ...action.userViewSettings.activeFilters ?? []
                ]
            };

            const path = `${ APP_CONFIG.baseWeb }user_attendance_time/${  getUrlParamsFromViewSettings( userViewSettings ) }`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject(path) ).pipe(
                map( response => fetchUserAttendanceTimeSuccess(
                    response.response as ApiReturnType< UserAttendanceTime[] >,
                    action.viewIdentifier
                ) ),
                catchError( error => of(
                    fetchUserAttendanceTimeError(error, action.viewIdentifier ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse( language, error.status, action.viewIdentifier, "fetchEntriesAction"))
                ) )
            )
        } )
    );



/**
 * UPDATE
 */
export interface UpdateUserAttendanceTimeAction extends ReduxAction<typeof UPDATE_USER_ATTENDANCE_TIME> {
    entry: UserAttendanceTime
    fetchFilters: Partial<UserViewSettings>
}
export interface UpdateUserAttendanceTimeSuccessAction extends ReduxAction<typeof UPDATE_USER_ATTENDANCE_TIME_SUCCESS> {

}
export interface UpdateUserAttendanceTimeErrorAction extends ErrorAction<typeof UPDATE_USER_ATTENDANCE_TIME_ERROR>{}

export type UpdateUserAttendanceTimeActionTypes = UpdateUserAttendanceTimeAction
    | UpdateUserAttendanceTimeSuccessAction
    | UpdateUserAttendanceTimeErrorAction

export const UPDATE_USER_ATTENDANCE_TIME = "UPDATE_USER_ATTENDANCE_TIME";
export const UPDATE_USER_ATTENDANCE_TIME_SUCCESS = "UPDATE_USER_ATTENDANCE_TIME_SUCCESS";
export const UPDATE_USER_ATTENDANCE_TIME_ERROR = "UPDATE_USER_ATTENDANCE_TIME_ERROR";

export const updateUserAttendanceTime = (
    entry: UserAttendanceTime,
    fetchFilters: Partial<UserViewSettings>
): UpdateUserAttendanceTimeAction => ({ type: UPDATE_USER_ATTENDANCE_TIME, entry, fetchFilters });
export const updateUserAttendanceTimeSuccess = (): UpdateUserAttendanceTimeSuccessAction =>
    ({ type: UPDATE_USER_ATTENDANCE_TIME_SUCCESS });
export const updateUserAttendanceTimeError = ( error: Error ): UpdateUserAttendanceTimeErrorAction =>
    ({ type: UPDATE_USER_ATTENDANCE_TIME_ERROR, error });

export const updateUserAttendanceTimeEpic = (
    action$: Observable< UpdateUserAttendanceTimeAction >,
    store$: StateObservable< AppState >
) => action$.pipe(
    ofType( UPDATE_USER_ATTENDANCE_TIME ),
    switchMap( action => {
        const path = `${APP_CONFIG.baseWeb}user_attendance_time/`;
        const viewIdentifier = UserAttendanceTimeModule.identifier;


        return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.put, {
            ...action.entry,
            start: action.entry.startAt.getTime(),
            end: action.entry.finishAt.getTime()
        } )).pipe(
            mergeMap( response => of(
                updateUserAttendanceTimeSuccess(),
                fetchUserAttendanceTime(
                    action.fetchFilters,
                    TimesheetModule.identifier
                ),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess )
            )),
            catchError( error => of(
                updateUserAttendanceTimeError(error),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse(
                    currentLanguageFromStore(store$),
                    error.status,
                    viewIdentifier,
                    "updateUserAttendanceTime"
                ))
            ) )
        )
    })
);



